import type { PublicWall, PublicWallPost } from "@brandwall/contracts";
import { arrange } from "@brandwall/widget/arrange";
import { type Report, renderWall } from "@brandwall/widget/wall";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Renders the exact same code that runs on brands' websites. With `onEdit`, the wall can be
 * arranged: a tap on a post edits it (size, hide), and posts can be dragged to new places.
 */
export function WallPreview({
  wall,
  onEdit,
  onMove,
  report,
  font = "'Schibsted Grotesk', system-ui, sans-serif",
}: {
  wall: PublicWall;
  /** The font when the wall's own is "inherit": the page around it. */
  font?: string;
  onEdit?: (post: PublicWallPost, tile: HTMLElement) => void;
  onMove?: (post: PublicWallPost, index: number) => void;
  /** An opened post's Report link sends through this; without it there's no link. */
  report?: Report;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const edit = useRef(onEdit);
  const move = useRef(onMove);
  edit.current = onEdit;
  move.current = onMove;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = el.shadowRoot ?? el.attachShadow({ mode: "open" });
    const editing = Boolean(edit.current);
    renderWall(
      root,
      wall,
      font,
      undefined,
      undefined,
      editing ? (p, t) => edit.current?.(p, t) : undefined,
      editing ? undefined : report,
    );
    if (!editing) return;
    const byId = (id: string) => wall.posts.find((p) => p.id === id);
    arrange(root, {
      onTap: (id, item) => {
        const p = byId(id);
        if (p) edit.current?.(p, item);
      },
      onMove: (id, index) => {
        const p = byId(id);
        if (p) move.current?.(p, index);
      },
    });
  }, [wall, font, report]);
  return <div ref={ref} />;
}

/** The Report form works as on the site in the preview, but nothing is sent. */
const previewReport: Report = async () => true;

/**
 * The wall full screen, as visitors see it on the brand's site: nothing to drag, and a click on a
 * post opens it like on the site.
 */
export function FullPreview({
  wall,
  phone = false,
  onClose,
}: {
  wall: PublicWall;
  /** At a phone's width. */
  phone?: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const el = ref.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const before = document.activeElement as HTMLElement | null;
    el?.querySelector<HTMLButtonElement>(".full-preview-bar button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      // Esc closes an open post first, and the preview after.
      if (e.key !== "Escape") return;
      if (el?.querySelector(".full-preview-wall > div")?.shadowRoot?.querySelector("dialog[open]"))
        return;
      close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      // Its moving tiles stop with it.
      for (const v of el
        ?.querySelector(".full-preview-wall > div")
        ?.shadowRoot?.querySelectorAll("video") ?? [])
        v.pause();
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
      before?.focus();
    };
  }, []);
  // On the page itself, so nothing around the editor can change where it sits.
  return createPortal(
    <div
      ref={ref}
      className="full-preview"
      role="dialog"
      aria-modal="true"
      aria-label="Preview of the wall"
      style={{ background: wall.design.background }}
    >
      <div className="full-preview-bar">
        <span style={{ flex: 1 }}>
          Preview: the wall as visitors see it. Click a post to open it. Reports made here aren't
          sent.
        </span>
        <button type="button" className="btn sm" onClick={onClose}>
          Close preview
        </button>
      </div>
      <div className={`full-preview-wall${phone ? " phone" : ""}`}>
        <WallPreview wall={wall} report={previewReport} />
      </div>
    </div>,
    document.body,
  );
}
